import { useState } from 'react';
import { Link } from 'react-router';
import { Avatar, Confirm, DataTable, ErrorState, Field, Modal, Spinner, StatusBadge } from '../../components/ui.jsx';
import { api, ApiError } from '../../api/client.js';
import { useFetch, useTitle } from '../../lib/hooks.js';
import { useToast } from '../../state/ToastContext.jsx';

/**
 * Shown once, right after a manager is invited. The password is hashed the
 * moment it is generated, so this is the only chance to read it.
 */
const PasswordHandover = ({ manager, password, onClose }) => {
  const toast = useToast();

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(password);
      toast.success('Copied.');
    } catch {
      toast.info('Copy it by hand: your browser blocked the clipboard.');
    }
  };

  return (
    <Modal
      title="Give this to the manager"
      onClose={onClose}
      footer={
        <button type="button" className="btn btn-primary" onClick={onClose}>
          I have passed it on
        </button>
      }
    >
      <p className="modal-lead">
        {manager.name} can now sign in with their email and this temporary password. They will be made to choose their
        own before they can do anything, so it stops working as soon as they use it.
      </p>

      <div className="handover">
        <span className="handover-label">Email</span>
        <strong>{manager.email}</strong>
        <span className="handover-label">Temporary password</span>
        <code className="handover-code">{password}</code>
      </div>

      <div className="row-actions">
        <button type="button" className="btn btn-outline btn-sm" onClick={copy}>
          Copy the password
        </button>
      </div>

      <p className="notice notice-warn">
        This is the only time it is shown. If it goes missing, revoke the manager and invite them again.
      </p>
    </Modal>
  );
};

const InviteManagerForm = ({ onCreated, onClose }) => {
  const [form, setForm] = useState({ name: '', email: '', phone: '' });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);

  const set = (patch) => setForm((current) => ({ ...current, ...patch }));

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setErrors({});
    try {
      const result = await api.post('/managers', {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim()
      });
      onCreated(result);
    } catch (error) {
      if (error instanceof ApiError && error.details) setErrors(error.details);
      else setErrors({ _: error.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title="Add a property manager"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" form="manager-form" className="btn btn-primary" disabled={busy}>
            {busy ? 'Inviting...' : 'Invite'}
          </button>
        </>
      }
    >
      <p className="modal-lead">
        A manager can do everything you can across your properties, leases, rent, tenants, applications and messages -
        except manage other managers or your own payment accounts. You will be given a temporary password to pass on.
      </p>

      {errors._ ? <div className="notice notice-bad">{errors._}</div> : null}

      <form id="manager-form" className="stack" onSubmit={submit}>
        <Field label="Full name" error={errors.name} required>
          <input
            type="text"
            value={form.name}
            onChange={(event) => set({ name: event.target.value })}
            placeholder="Ngwa Emmanuel"
            required
          />
        </Field>

        <div className="grid-2">
          <Field label="Email" hint="They sign in with this." error={errors.email} required>
            <input type="email" value={form.email} onChange={(event) => set({ email: event.target.value })} required />
          </Field>
          <Field label="Phone number" error={errors.phone} required>
            <input
              type="tel"
              value={form.phone}
              onChange={(event) => set({ phone: event.target.value })}
              placeholder="+237 6 70 00 00 00"
              required
            />
          </Field>
        </div>
      </form>
    </Modal>
  );
};

/**
 * What this manager is in charge of.
 *
 * A manager has always been a straight stand-in for the landlord, and that stays
 * the default. Narrowing them is the new option, and it exists so a landlord can
 * hire somebody to read the electricity meter without handing over their leases,
 * their rent and their tenants along with it.
 */
const DutiesForm = ({ manager, services, onSubmit, onClose }) => {
  const [mode, setMode] = useState(manager.accessMode || 'full');
  const [capabilities, setCapabilities] = useState(new Set(manager.capabilities || []));
  const [serviceIds, setServiceIds] = useState(new Set(manager.serviceIds || []));
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);

  const toggle = (set, update) => (value) => {
    const next = new Set(set);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    update(next);
  };

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setErrors({});
    try {
      await onSubmit({
        accessMode: mode,
        capabilities: [...capabilities],
        serviceIds: [...serviceIds]
      });
    } catch (error) {
      if (error instanceof ApiError && error.details) setErrors(error.details);
      else setErrors({ _: error.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title={`What ${manager.name} does`}
      onClose={onClose}
      wide
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" form="manager-duties-form" className="btn btn-primary" disabled={busy}>
            {busy ? 'Saving...' : 'Save'}
          </button>
        </>
      }
    >
      {errors._ ? <div className="notice notice-bad">{errors._}</div> : null}

      <form id="manager-duties-form" className="stack" onSubmit={submit}>
        <label className="check check-block">
          <input type="radio" name="access-mode" checked={mode === 'full'} onChange={() => setMode('full')} />
          <span>
            <strong>Everything on my account</strong>
            <em>Listings, leases, rent and tenants - but never your other managers or your payment accounts.</em>
          </span>
        </label>

        <label className="check check-block">
          <input
            type="radio"
            name="access-mode"
            checked={mode === 'restricted'}
            onChange={() => setMode('restricted')}
          />
          <span>
            <strong>Only what I tick below</strong>
            <em>Anything not ticked is refused, in the browser and at the API.</em>
          </span>
        </label>

        {mode === 'restricted' ? (
          <div className="duty-list">
            {errors.capabilities ? <div className="notice notice-bad">{errors.capabilities}</div> : null}

            <label className="check">
              <input
                type="checkbox"
                checked={capabilities.has('portfolio')}
                onChange={() => toggle(capabilities, setCapabilities)('portfolio')}
              />
              <span>Properties, units and photos</span>
            </label>

            <label className="check">
              <input
                type="checkbox"
                checked={capabilities.has('leases_rent')}
                onChange={() => toggle(capabilities, setCapabilities)('leases_rent')}
              />
              <span>Leases, rent and tenants</span>
            </label>

            <label className="check">
              <input
                type="checkbox"
                checked={capabilities.has('utilities')}
                onChange={() => toggle(capabilities, setCapabilities)('utilities')}
              />
              <span>Utility services</span>
            </label>

            {capabilities.has('utilities') ? (
              <div className="duty-services">
                {errors.serviceIds ? <div className="notice notice-bad">{errors.serviceIds}</div> : null}
                {services.length === 0 ? (
                  <p className="muted">
                    You have no utility services yet. Add one under Utilities and it will appear here.
                  </p>
                ) : (
                  services.map((service) => (
                    <label className="check" key={service.id}>
                      <input
                        type="checkbox"
                        checked={serviceIds.has(service.id)}
                        onChange={() => toggle(serviceIds, setServiceIds)(service.id)}
                      />
                      <span>{service.name}</span>
                    </label>
                  ))
                )}
              </div>
            ) : null}
          </div>
        ) : null}
      </form>
    </Modal>
  );
};

const Team = () => {
  useTitle('Team');
  const toast = useToast();
  const { data, loading, error, reload } = useFetch('/managers');

  const [inviting, setInviting] = useState(false);
  const [handover, setHandover] = useState(null);
  const [confirming, setConfirming] = useState(null);
  const [duties, setDuties] = useState(null);
  const [busy, setBusy] = useState(false);

  const toggleAccess = async () => {
    if (!confirming) return;
    setBusy(true);
    try {
      const action = confirming.manager.isActive ? 'revoke' : 'reactivate';
      await api.post(`/managers/${confirming.manager.id}/${action}`);
      toast.success(action === 'revoke' ? 'Access revoked.' : 'Access restored.');
      setConfirming(null);
      reload();
    } catch (requestError) {
      toast.error(requestError.message);
    } finally {
      setBusy(false);
    }
  };

  if (loading && !data) return <Spinner />;
  if (error) {
    return (
      <div className="container section">
        <ErrorState message={error} onRetry={reload} />
      </div>
    );
  }

  const items = data.items || [];
  const services = data.services || [];

  const dutySummary = (manager) => {
    if (manager.accessMode !== 'restricted') return 'Everything on your account';

    const parts = [];
    if (manager.capabilities.includes('portfolio')) parts.push('Properties');
    if (manager.capabilities.includes('leases_rent')) parts.push('Leases and rent');
    if (manager.capabilities.includes('utilities')) {
      const names = services
        .filter((service) => manager.serviceIds.includes(service.id))
        .map((service) => service.name);
      parts.push(names.length > 0 ? names.join(', ') : 'Utilities');
    }
    return parts.join(' - ') || 'Nothing yet';
  };

  const columns = [
    {
      key: 'name',
      header: 'Manager',
      render: (manager) => (
        <div className="cell-listing">
          <Avatar name={manager.name} url={manager.avatarUrl} size={40} />
          <div>
            <strong>{manager.name}</strong>
            <span className="muted">{manager.phone}</span>
            <span className="muted">{manager.email}</span>
          </div>
        </div>
      )
    },
    {
      key: 'state',
      header: 'Access',
      render: (manager) =>
        manager.isActive ? (
          manager.mustChangePassword ? (
            <StatusBadge status="pending" label="Invited, not signed in yet" />
          ) : (
            <StatusBadge status="approved" label="Active" />
          )
        ) : (
          <StatusBadge status="rejected" label="Revoked" />
        )
    },
    {
      key: 'duties',
      header: 'In charge of',
      render: (manager) => (
        <div className="cell-stack">
          <span>{dutySummary(manager)}</span>
          <button type="button" className="link-btn" onClick={() => setDuties(manager)}>
            Change
          </button>
        </div>
      )
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (manager) => (
        <div className="cell-actions">
          <Link to={`/landlord/team/${manager.id}/activity`} className="btn btn-outline btn-sm">
            Activity
          </Link>
          <button
            type="button"
            className={`link-btn ${manager.isActive ? 'link-btn-danger' : ''}`}
            onClick={() => setConfirming({ manager })}
          >
            {manager.isActive ? 'Revoke access' : 'Restore access'}
          </button>
        </div>
      )
    }
  ];

  return (
    <div className="container section">
      <header className="page-head">
        <div>
          <h1>Team</h1>
          <p className="muted">
            Property managers you have added, and what they can see and do. {items.length} in total.
          </p>
        </div>
        <div className="row-actions">
          <Link to="/landlord/team/activity" className="btn btn-outline">
            All activity
          </Link>
          <button type="button" className="btn btn-primary" onClick={() => setInviting(true)}>
            Add a manager
          </button>
        </div>
      </header>

      <DataTable
        columns={columns}
        rows={items}
        empty={{
          title: 'No managers yet',
          message:
            'Add someone to help run your properties day to day. By default they get your listings, leases, rent and tenants - never your other managers or your payment accounts - and you can narrow that to one job, such as reading a single meter.'
        }}
      />

      {duties ? (
        <DutiesForm
          manager={duties}
          services={services}
          onClose={() => setDuties(null)}
          onSubmit={async (payload) => {
            await api.put(`/managers/${duties.id}/access`, payload);
            toast.success('Saved. It takes effect on their very next click.');
            setDuties(null);
            reload();
          }}
        />
      ) : null}

      {inviting ? (
        <InviteManagerForm
          onClose={() => setInviting(false)}
          onCreated={(result) => {
            setInviting(false);
            reload();
            setHandover({ manager: result.manager, password: result.temporaryPassword });
          }}
        />
      ) : null}

      {handover ? (
        <PasswordHandover
          manager={handover.manager}
          password={handover.password}
          onClose={() => {
            setHandover(null);
            reload();
          }}
        />
      ) : null}

      {confirming ? (
        <Confirm
          title={confirming.manager.isActive ? 'Revoke this manager?' : 'Restore this manager?'}
          message={
            confirming.manager.isActive
              ? `${confirming.manager.name} will be signed out immediately and will not be able to sign back in until you restore access.`
              : `${confirming.manager.name} will be able to sign in again with their existing password.`
          }
          confirmLabel={confirming.manager.isActive ? 'Revoke access' : 'Restore access'}
          tone={confirming.manager.isActive ? 'danger' : 'primary'}
          busy={busy}
          onConfirm={toggleAccess}
          onClose={() => setConfirming(null)}
        />
      ) : null}
    </div>
  );
};

export default Team;
